import { useState, useCallback, useRef, useEffect, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { ConfirmModal } from "@/components/ui";
import { FileDropzone } from "@/components/common/FileDropzone";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import {
  FILE_VALIDATION_PRESETS,
  formatFileSizeLimit,
  partitionValidatedFiles,
  toastRejectedFiles,
} from "@/lib/fileValidation";
import { getStorageUploadErrorMessage } from "@/lib/storageUpload";
import { isStorageProxyUrl } from "@/lib/storageFetch";
import { cn } from "@/lib/utils";
import { storageService } from "@/services/storageService";
import type { MediaItem } from "@/types";
import type { MediaCarouselProps, UploadingItem } from "./types";
import { generateVideoThumbnail, getThumbnailUrl, uploadVideoThumbnail } from "./utils";
import { Thumbnail } from "./Thumbnail";
import { MainViewer } from "./MainViewer";
import { MediaCarouselEmpty } from "./MediaCarouselEmpty";
import { ImageCropModal } from "@/components/common/ImageCropModal";
import { useImageCropSession } from "@/hooks/useImageCropSession";

function shouldDeferArrowKeysToNativeControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return true;
  }
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  ) {
    return true;
  }
  return target.closest("video, audio") != null;
}

export function MediaCarousel({
  media,
  onChange,
  onAutoSave,
  activityId,
  editable,
  className,
}: MediaCarouselProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("activities");
  const { t: tComponents } = useTranslation("components");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [uploadingItems, setUploadingItems] = useState<UploadingItem[]>([]);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingBlobUrlsRef = useRef<Set<string>>(new Set());
  const {
    session: cropSession,
    requestImageCrop,
    resolveCrop,
  } = useImageCropSession();

  useEffect(() => {
    return () => {
      for (const url of pendingBlobUrlsRef.current) {
        URL.revokeObjectURL(url);
      }
      pendingBlobUrlsRef.current.clear();
    };
  }, []);

  const sortedMedia = [...media].sort((a, b) => a.order - b.order);
  const isUploading = uploadingItems.length > 0;
  const isEmpty = sortedMedia.length === 0 && uploadingItems.length === 0;

  const handleCarouselKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (sortedMedia.length <= 1) {
        return;
      }
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }
      if (shouldDeferArrowKeysToNativeControl(event.target)) {
        return;
      }

      event.preventDefault();
      if (event.key === "ArrowLeft") {
        setCurrentIndex((prev) =>
          prev > 0 ? prev - 1 : sortedMedia.length - 1,
        );
      } else {
        setCurrentIndex((prev) =>
          prev < sortedMedia.length - 1 ? prev + 1 : 0,
        );
      }
    },
    [sortedMedia.length],
  );

  // Helper to update media and auto-save
  const updateMedia = useCallback(
    async (newMedia: MediaItem[]) => {
      onChange(newMedia);
      try {
        await onAutoSave(newMedia);
      } catch (error) {
        console.error("Auto-save failed:", error);
        toastApiError(error, "generic", t("media.saveChangesError"));
      }
    },
    [onChange, onAutoSave, t],
  );

  const handleAddMedia = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const { accepted, rejected } = partitionValidatedFiles(
        files,
        FILE_VALIDATION_PRESETS.activityMedia,
      );

      toastRejectedFiles(rejected, {
        tooLarge: (_file, maxSize) =>
          tComponents("mediaUploadMax", {
            max: formatFileSizeLimit(maxSize),
          }),
        unsupported: () => tComponents("mediaUploadEither"),
      });

      if (accepted.length === 0) {
        return;
      }

      // Crop images one-by-one (16:9); videos pass through unchanged.
      const prepared: Array<{ file: File; kind: "image" | "video" }> = [];
      for (const item of accepted) {
        if (item.kind === "video") {
          prepared.push({ file: item.file, kind: "video" });
          continue;
        }
        const cropped = await requestImageCrop(item.file);
        if (cropped) {
          prepared.push({ file: cropped, kind: "image" });
        }
      }

      if (prepared.length === 0) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      const newItems: UploadingItem[] = prepared.map(({ file, kind }) => {
        const localUrl = URL.createObjectURL(file);
        pendingBlobUrlsRef.current.add(localUrl);
        return {
          localUrl,
          type: kind,
          progress: 0,
          phase: "uploading",
        };
      });
      setUploadingItems((prev) => [...prev, ...newItems]);

      for (const item of newItems) {
        if (item.type !== "video") {
          continue;
        }
        void generateVideoThumbnail(item.localUrl)
          .then((thumbnailUrl) => {
            setUploadingItems((prev) =>
              prev.map((entry) =>
                entry.localUrl === item.localUrl
                  ? { ...entry, thumbnailUrl }
                  : entry,
              ),
            );
          })
          .catch(() => undefined);
      }

      const uploaded: MediaItem[] = [];

      for (let i = 0; i < prepared.length; i++) {
        const { file, kind } = prepared[i];
        const uploadItem = newItems[i];

        try {
          const publicUrl = await storageService.uploadFile(
            "activities",
            activityId,
            file,
            {
              onProgress: (progressState) => {
                setUploadingItems((prev) =>
                  prev.map((item) =>
                    item.localUrl === uploadItem.localUrl
                      ? { ...item, ...progressState }
                      : item,
                  ),
                );
              },
            },
          );

          let thumbnail_url: string | undefined;
          if (kind === "video") {
            try {
              thumbnail_url = await uploadVideoThumbnail(
                activityId,
                uploadItem.localUrl,
                file.name,
              );
            } catch (thumbError) {
              console.error("Video thumbnail failed:", thumbError);
              try {
                await storageService.deleteFile(publicUrl);
              } catch {
                // Best-effort cleanup of the uploaded video when thumbnail fails.
              }
              throw thumbError;
            }
          }

          uploaded.push({
            url: publicUrl,
            type: kind,
            order: media.length + uploaded.length,
            thumbnail_url,
          });

          setUploadingItems((prev) => {
            const item = prev.find(
              (entry) => entry.localUrl === uploadItem.localUrl,
            );
            if (item) {
              pendingBlobUrlsRef.current.delete(item.localUrl);
              URL.revokeObjectURL(item.localUrl);
            }
            return prev.filter(
              (entry) => entry.localUrl !== uploadItem.localUrl,
            );
          });
        } catch (error) {
          console.error("Upload failed:", error);
          toast.error(getStorageUploadErrorMessage(error, tComponents, file.name));
          setUploadingItems((prev) => {
            const item = prev.find(
              (entry) => entry.localUrl === uploadItem.localUrl,
            );
            if (item) {
              pendingBlobUrlsRef.current.delete(item.localUrl);
              URL.revokeObjectURL(item.localUrl);
            }
            return prev.filter(
              (entry) => entry.localUrl !== uploadItem.localUrl,
            );
          });
        }
      }

      if (uploaded.length > 0) {
        const newMedia = [...media, ...uploaded];
        await updateMedia(newMedia);
        toast.success(t("media.uploadedCount", { count: uploaded.length }));
      }

      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [media, updateMedia, activityId, t, tComponents, requestImageCrop],
  );

  const handleRemove = useCallback(
    async (index: number) => {
      const currentSortedMedia = [...media].sort((a, b) => a.order - b.order);
      const item = currentSortedMedia[index];
      if (!item) return;

      // Optimistic update: remove from UI immediately
      const newMedia = currentSortedMedia
        .filter((_, i) => i !== index)
        .map((m, i) => ({ ...m, order: i }));

      // Update UI immediately
      onChange(newMedia);

      // Adjust current index if needed
      if (index <= currentIndex && currentIndex > 0) {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
      }

      // Delete from storage and save in background
      try {
        if (isStorageProxyUrl(item.url)) {
          await storageService.deleteFile(item.url);
        }
        await onAutoSave(newMedia);
        toast.success(t("media.deleted"));
      } catch (error) {
        console.error("Delete failed:", error);
        toastApiError(error, "generic", t("media.deleteFailed"));
        // Rollback: restore the item
        onChange(currentSortedMedia);
      }
    },
    [media, onChange, onAutoSave, currentIndex, t],
  );

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteIndex === null) {
      return;
    }
    setIsDeleting(true);
    try {
      await handleRemove(pendingDeleteIndex);
      setPendingDeleteIndex(null);
    } finally {
      setIsDeleting(false);
    }
  }, [handleRemove, pendingDeleteIndex]);

  // Drag & drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newMedia = [...sortedMedia];
    const [dragged] = newMedia.splice(draggedIndex, 1);
    newMedia.splice(dropIndex, 0, dragged);

    const reordered = newMedia.map((item, i) => ({ ...item, order: i }));
    await updateMedia(reordered);

    setCurrentIndex(dropIndex);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    // Reset everything when drag ends
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const currentMedia = sortedMedia[currentIndex];

  return (
    <>
      {isEmpty ? (
        <MediaCarouselEmpty
          editable={editable}
          className={className}
          onSelectFiles={handleAddMedia}
        />
      ) : (
        <div
          className={cn("space-y-4", className)}
          onKeyDown={handleCarouselKeyDown}
        >
          {currentMedia && (
            <MainViewer
              media={currentMedia}
              currentIndex={currentIndex}
              totalCount={sortedMedia.length}
              editable={editable}
              onPrev={() =>
                setCurrentIndex((p) =>
                  p > 0 ? p - 1 : sortedMedia.length - 1,
                )
              }
              onNext={() =>
                setCurrentIndex((p) =>
                  p < sortedMedia.length - 1 ? p + 1 : 0,
                )
              }
              onRemove={() => setPendingDeleteIndex(currentIndex)}
            />
          )}

          <div className="flex gap-2.5 overflow-x-auto p-1.5">
            {sortedMedia.map((item, index) => (
              <Thumbnail
                key={item.url}
                src={getThumbnailUrl(item)}
                alt={t("media.thumbnailAlt", { n: index + 1 })}
                isVideo={item.type === "video"}
                isSelected={index === currentIndex}
                isDragging={draggedIndex === index}
                isDragOver={dragOverIndex === index}
                editable={editable}
                onClick={() => setCurrentIndex(index)}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              />
            ))}

            {uploadingItems.map((item, index) => (
              <Thumbnail
                key={item.localUrl}
                src={item.thumbnailUrl ?? item.localUrl}
                alt={t("media.uploadingAlt", { n: index + 1 })}
                isVideo={item.type === "video"}
                isUploading
                upload={item}
                isSelected={false}
                isDragging={false}
                isDragOver={false}
                editable={false}
                onClick={() => {}}
                onDragStart={() => {}}
                onDragOver={() => {}}
                onDragLeave={() => {}}
                onDrop={() => {}}
                onDragEnd={() => {}}
              />
            ))}

            {editable ? (
              <FileDropzone
                id={`media-carousel-add-${activityId}`}
                accept={FILE_ACCEPT.imageAndVideo}
                multiple
                inputRef={fileInputRef}
                disabled={isUploading}
                isLoading={isUploading}
                spinnerSize="sm"
                icon={Plus}
                iconClassName="h-5 w-5 text-surface-400 hover:text-primary-500"
                className={cn(
                  "h-16 w-20 shrink-0 gap-0 rounded-lg px-0 py-0 opacity-80 transition-opacity hover:opacity-100",
                  isUploading && "cursor-not-allowed bg-surface-50",
                )}
                onFilesSelected={(files) => {
                  void handleAddMedia(files);
                }}
              />
            ) : null}
          </div>

          {sortedMedia.length > 1 && (
            <p className="text-center text-xs text-surface-400">
              {t("media.arrowNavHint")}
            </p>
          )}

          <ConfirmModal
            closeLabel={tCommon("close")}
            cancelText={tCommon("cancel")}
            isOpen={pendingDeleteIndex !== null}
            onClose={() => {
              if (isDeleting) {
                return;
              }
              setPendingDeleteIndex(null);
            }}
            onConfirm={() => void confirmDelete()}
            title={t("media.deleteConfirmTitle")}
            message={t("media.deleteConfirmMessage")}
            confirmText={tCommon("delete")}
            isDestructive
            isLoading={isDeleting}
          />
        </div>
      )}

      <ImageCropModal
        session={cropSession}
        aspect={16 / 9}
        cropAreaClassName="storefront-crop-area"
        labels={{
          title: tComponents("imageCrop.title"),
          description: t("media.cropDescription"),
          confirm: tComponents("imageCrop.confirm"),
          cancel: tComponents("imageCrop.cancel"),
          zoom: tComponents("imageCrop.zoom"),
          failed: tComponents("imageCrop.failed"),
        }}
        onConfirm={resolveCrop}
        onCancel={() => resolveCrop(null)}
      />
    </>
  );
}
